import * as THREE from 'three';
import { color, type PaletteName } from './palette';

/**
 * Geometrie-primitieven voor de meshkit.
 *
 * Alles wordt hier met exacte vertices opgebouwd — dit is de laag die in het oorspronkelijke
 * project Blender was. Alle geometrie is non-indexed en flat shaded: elke facet heeft zijn
 * eigen normaal, wat precies de gefacetteerde look uit de art direction geeft.
 *
 * Conventie: alle primitieven staan gecentreerd op de oorsprong tenzij anders vermeld.
 * Verplaatsen doe je met `at()` of `THREE.BufferGeometry.translate`.
 */

/** Standaard bevelbreedte in meters, uit docs/ART_DIRECTION.md (0.02–0.05 m). */
export const DEFAULT_BEVEL = 0.03;

/** Een stuk geometrie met zijn kleurzone. De bouwsteen voor `mergeParts`. */
export interface Part {
  geometry: THREE.BufferGeometry;
  color: PaletteName;
  /**
   * Materiaaltoon als vermenigvuldiger op de paletkleur (1 = ongewijzigd). Uitsluitend voor
   * een tweede tint van hetzelfde materiaal — voering van een jas, binnenkant van een
   * hoedrand, omgeslagen manchet — nooit voor ingebakken licht of schaduw: dat doet de
   * runtime-verlichting. Gebruik de constanten `LINING` en `WORN`, geen losse getallen.
   */
  shade?: number;
}

/** Voering, binnenkant, omgeslagen rand: hetzelfde materiaal in de schaduwtint. */
export const LINING = 0.62;
/** Versleten of opgelichte variant van dezelfde stof: een revers, een naad, een kraagrand. */
export const WORN = 1.14;

export function part(geometry: THREE.BufferGeometry, colorName: PaletteName, shade = 1): Part {
  return { geometry, color: colorName, shade };
}

/** Verplaats geometrie en geef hem terug — bedoeld om inline te ketenen. */
export function at<T extends THREE.BufferGeometry>(geometry: T, x: number, y: number, z: number): T {
  geometry.translate(x, y, z);
  return geometry;
}

/** Rechte doos zonder bevel: 12 driehoeken. Gebruik dit voor massa's die je niet ziet. */
export function box(width: number, height: number, depth: number): THREE.BufferGeometry {
  return new THREE.BoxGeometry(width, height, depth).toNonIndexed();
}

export interface TaperedBoxOptions {
  readonly bottomWidth: number;
  readonly topWidth: number;
  readonly height: number;
  readonly bottomDepth: number;
  readonly topDepth: number;
  readonly bottomOffsetX?: number;
  readonly topOffsetX?: number;
  readonly bottomOffsetZ?: number;
  readonly topOffsetZ?: number;
}

export interface FacetedLoftRing {
  readonly y: number;
  readonly width: number;
  readonly depth: number;
  readonly offsetX?: number;
  readonly offsetZ?: number;
  /**
   * Vorm van de doorsnede: 0 is een ellips, 1 een bijna rechthoekige superellips met ronde
   * hoeken. Een kaak, een borstkas of een schoenneus is geen ellips; dit is de knop die het
   * verschil maakt tussen een buis en een lichaamsdeel.
   */
  readonly squareness?: number;
  /**
   * Aandeel van `depth` dat vóór het ringmiddelpunt ligt (0.5 is symmetrisch). Een schedel
   * heeft een platte voorkant en een rond achterhoofd; een kuit bolt naar achteren.
   */
  readonly frontBias?: number;
  /** Eigen halve spleethoek voor deze ring; alleen zinvol als de loft een `gap` heeft. */
  readonly gap?: number;
}

export interface FacetedLoftOptions {
  readonly rings: readonly FacetedLoftRing[];
  readonly sides?: number;
  readonly phase?: number;
  readonly capTop?: boolean;
  readonly capBottom?: boolean;
  /** Standaard-`squareness` voor ringen die er zelf geen opgeven. */
  readonly squareness?: number;
  /**
   * Halve openingshoek in radialen van een spleet aan de voorkant (+Z). Met een spleet is
   * de loft een open schaal — geen caps — en lopen de `sides` punten van `gap` tot
   * `2π − gap`. Dit is hoe een open jas zijn voorpanden krijgt.
   */
  readonly gap?: number;
  /** Keer de winding om, voor binnenwanden (voering) die van binnenuit zichtbaar zijn. */
  readonly flip?: boolean;
}

export interface ShellLoftOptions extends FacetedLoftOptions {
  /** Wanddikte: de binnenwand ligt zoveel naar binnen. */
  readonly thickness: number;
}

export interface ShellLoft {
  /** Buitenwand, winding naar buiten. */
  readonly outer: THREE.BufferGeometry;
  /** Binnenwand (voering), winding naar binnen. */
  readonly inner: THREE.BufferGeometry;
  /** Randen die binnen en buiten verbinden: zoom, bovenrand en de twee voorranden bij een spleet. */
  readonly rim: THREE.BufferGeometry;
}

export interface WedgeOptions {
  readonly backWidth: number;
  readonly frontWidth: number;
  readonly height: number;
  readonly depth: number;
  readonly backHeight?: number;
  readonly frontHeight?: number;
  readonly backOffsetX?: number;
  readonly frontOffsetX?: number;
}

/**
 * Afgeknotte doos rond de oorsprong: hetzelfde budget als `box`, maar bruikbaar voor
 * ledematen, torso's, schoenen en andere vormen die naar een gewricht toe versmallen.
 */
export function taperedBox(options: TaperedBoxOptions): THREE.BufferGeometry {
  const bottomY = -options.height / 2;
  const topY = options.height / 2;
  const bw = options.bottomWidth / 2;
  const tw = options.topWidth / 2;
  const bd = options.bottomDepth / 2;
  const td = options.topDepth / 2;
  const bx = options.bottomOffsetX ?? 0;
  const tx = options.topOffsetX ?? 0;
  const bz = options.bottomOffsetZ ?? 0;
  const tz = options.topOffsetZ ?? 0;

  const bfl = [bx - bw, bottomY, bz + bd];
  const bfr = [bx + bw, bottomY, bz + bd];
  const bbr = [bx + bw, bottomY, bz - bd];
  const bbl = [bx - bw, bottomY, bz - bd];
  const tfl = [tx - tw, topY, tz + td];
  const tfr = [tx + tw, topY, tz + td];
  const tbr = [tx + tw, topY, tz - td];
  const tbl = [tx - tw, topY, tz - td];

  const triangles: number[][] = [
    ...quad(bfl, bfr, bbr, bbl),
    ...quad(tfr, tfl, tbl, tbr),
    ...quad(bfr, bfl, tfl, tfr),
    ...quad(bbl, bbr, tbr, tbl),
    ...quad(bfl, bbl, tbl, tfl),
    ...quad(bbr, bfr, tfr, tbr),
  ];

  return fromTriangles(triangles, false);
}

/**
 * Gefacetteerde loft door horizontale doorsnede-ringen.
 *
 * Dit is de character-helper voor anatomische volumes: romp, schedel, haar, armen en benen
 * krijgen meerdere meetbare ringen in plaats van één primitieve. De ringpunten volgen een
 * ellips en blijven flat shaded/non-indexed, zodat het low-poly karakter zichtbaar blijft.
 */
export function facetedLoft(options: FacetedLoftOptions): THREE.BufferGeometry {
  const sides = options.sides ?? 12;
  if (sides < 3) throw new Error('facetedLoft heeft minstens 3 zijden nodig');
  if (options.rings.length < 2) throw new Error('facetedLoft heeft minstens 2 ringen nodig');

  for (const ring of options.rings) {
    if (
      !Number.isFinite(ring.y) ||
      !Number.isFinite(ring.width) ||
      !Number.isFinite(ring.depth) ||
      ring.width <= 0 ||
      ring.depth <= 0
    ) {
      throw new Error('facetedLoft kreeg een ongeldige ring');
    }
  }

  const open = (options.gap ?? 0) > 0;
  const points = loftPoints(options);
  const triangles: number[][] = [];
  pushLoftWalls(triangles, points, open);

  if (!open && (options.capBottom ?? true)) {
    const ring = points[0]!;
    const center = centerOfRing(options.rings[0]!);
    for (let side = 0; side < sides; side++) {
      const next = (side + 1) % sides;
      triangles.push([center, ring[next]!, ring[side]!].flat());
    }
  }

  if (!open && (options.capTop ?? true)) {
    const ring = points[points.length - 1]!;
    const center = centerOfRing(options.rings[options.rings.length - 1]!);
    for (let side = 0; side < sides; side++) {
      const next = (side + 1) % sides;
      triangles.push([center, ring[side]!, ring[next]!].flat());
    }
  }

  if (options.flip) flipWinding(triangles);
  return fromTriangles(triangles, false);
}

/**
 * Loft met wanddikte: een buitenwand, een naar binnen gekeerde binnenwand en de randen
 * die ze verbinden. Dit is de bouwsteen voor kleding die open hangt — een jas waarvan je
 * de voering ziet bij de voorpanden en de zoom. Zonder binnenwand kijk je door een open
 * jas heen in het niets, omdat materialen enkelzijdig zijn.
 *
 * De drie geometrieën komen los terug zodat ze elk hun eigen kleurzone krijgen in
 * `mergeParts` (buitenkant leer, voering donker, randen leer).
 */
export function shellLoft(options: ShellLoftOptions): ShellLoft {
  const t = options.thickness;
  const innerRings = options.rings.map((ring) => ({
    ...ring,
    width: Math.max(ring.width - 2 * t, ring.width * 0.2),
    depth: Math.max(ring.depth - 2 * t, ring.depth * 0.2),
  }));
  const outerOptions: FacetedLoftOptions = { ...options, capTop: false, capBottom: false, flip: false };
  const innerOptions: FacetedLoftOptions = { ...outerOptions, rings: innerRings, flip: true };

  const outerPoints = loftPoints(outerOptions);
  const innerPoints = loftPoints(innerOptions);
  const open = (options.gap ?? 0) > 0;
  const sides = outerPoints[0]!.length;
  const last = outerPoints.length - 1;

  const rim: number[][] = [];
  const add = (a: number[], b: number[], c: number[], d: number[], toward: number[]) => {
    for (const triangle of quad(a, b, c, d)) rim.push(orient(triangle, toward));
  };

  // Zoom en bovenrand.
  for (let side = 0; side < (open ? sides - 1 : sides); side++) {
    const next = (side + 1) % sides;
    add(outerPoints[0]![side]!, outerPoints[0]![next]!, innerPoints[0]![next]!, innerPoints[0]![side]!, [0, -1, 0]);
    add(outerPoints[last]![side]!, outerPoints[last]![next]!, innerPoints[last]![next]!, innerPoints[last]![side]!, [0, 1, 0]);
  }

  // Voorranden van de spleet: de rand kijkt naar de opening, dus naar de voorkant toe.
  if (open) {
    for (let ringIndex = 0; ringIndex < last; ringIndex++) {
      for (const edge of [0, sides - 1]) {
        const o0 = outerPoints[ringIndex]![edge]!;
        const o1 = outerPoints[ringIndex + 1]![edge]!;
        const i0 = innerPoints[ringIndex]![edge]!;
        const i1 = innerPoints[ringIndex + 1]![edge]!;
        const ring = options.rings[ringIndex]!;
        add(o0, o1, i1, i0, [(ring.offsetX ?? 0) - o0[0]!, 0, (ring.offsetZ ?? 0) + ring.depth - o0[2]!]);
      }
    }
  }

  return {
    outer: facetedLoft(outerOptions),
    inner: facetedLoft(innerOptions),
    rim: fromTriangles(rim, false),
  };
}

/**
 * Platte veelhoek in het XZ-vlak, kijkend omhoog (+Y). Draai hem met `rotateX(π/2)` om
 * hem naar voren te laten kijken. Voor ogen, oren, knopen en andere kleine vlakken die
 * met een doos te hard zouden lezen.
 */
export function facetedDisc(width: number, depth: number, sides = 8, squareness = 0): THREE.BufferGeometry {
  const ring = loftPoints({ rings: [{ y: 0, width, depth, squareness }], sides })[0]!;
  const triangles: number[][] = [];
  for (let side = 0; side < sides; side++) {
    const next = (side + 1) % sides;
    triangles.push([[0, 0, 0], ring[side]!, ring[next]!].flat());
  }
  return fromTriangles(triangles, false);
}

/**
 * Z-positie van het ringoppervlak op een gegeven x, aan de voorkant (+Z). Hiermee zet je
 * een oog of neus exact óp een gezichtsring in plaats van erin of ervoor te gokken.
 */
export function ringFrontZ(ring: FacetedLoftRing, x: number, defaultSquareness = 0): number {
  const n = 2 + (ring.squareness ?? defaultSquareness) * 4;
  const u = Math.min(1, Math.abs((x - (ring.offsetX ?? 0)) / (ring.width * 0.5)));
  const unit = Math.pow(Math.max(0, 1 - Math.pow(u, n)), 1 / n);
  return (ring.offsetZ ?? 0) + unit * ring.depth * (ring.frontBias ?? 0.5);
}

/**
 * Langwerpige wig met een lage achterkant en smallere/neerliggende voorkant. Geschikt voor
 * schoenen, kraagpunten, revers, haarplukken en andere vormen die een duidelijke richting
 * in het silhouet nodig hebben.
 */
export function wedge(options: WedgeOptions): THREE.BufferGeometry {
  const backHeight = options.backHeight ?? options.height;
  const frontHeight = options.frontHeight ?? options.height * 0.55;
  const y0 = -options.height / 2;
  const zBack = -options.depth / 2;
  const zFront = options.depth / 2;
  const backX = options.backOffsetX ?? 0;
  const frontX = options.frontOffsetX ?? 0;
  const bw = options.backWidth / 2;
  const fw = options.frontWidth / 2;

  const bbl = [backX - bw, y0, zBack];
  const bbr = [backX + bw, y0, zBack];
  const fbr = [frontX + fw, y0, zFront];
  const fbl = [frontX - fw, y0, zFront];
  const btl = [backX - bw, y0 + backHeight, zBack];
  const btr = [backX + bw, y0 + backHeight, zBack];
  const ftr = [frontX + fw, y0 + frontHeight, zFront];
  const ftl = [frontX - fw, y0 + frontHeight, zFront];

  return fromTriangles(
    [
      ...quad(bbl, bbr, fbr, fbl),
      ...quad(btl, ftl, ftr, btr),
      ...quad(bbr, bbl, btl, btr),
      ...quad(fbl, fbr, ftr, ftl),
      ...quad(bbl, fbl, ftl, btl),
      ...quad(fbr, bbr, btr, ftr),
    ],
    false,
  );
}

/**
 * Doos met afgeschuinde randen: 6 vlakken, 12 randen, 8 hoeken — 44 driehoeken.
 *
 * De bevel is het visuele werkpaard van deze stijl: hij vangt licht op de randen en maakt
 * dat een massa als gefacetteerd volume leest in plaats van als platte kubus. Reserveer hem
 * voor wat de speler van dichtbij ziet; verre gevelmassa's kunnen met `box`.
 */
export function beveledBox(
  width: number,
  height: number,
  depth: number,
  bevel: number = DEFAULT_BEVEL,
): THREE.BufferGeometry {
  const half: [number, number, number] = [width / 2, height / 2, depth / 2];
  const b = Math.min(bevel, Math.min(...half) * 0.99);
  const inset: [number, number, number] = [half[0] - b, half[1] - b, half[2] - b];

  const triangles: number[][] = [];

  const point = (axis: number, sign: number, value: number, u: number, v: number): number[] => {
    const p = [0, 0, 0];
    const [uAxis, vAxis] = otherAxes(axis);
    p[axis] = value * sign;
    p[uAxis] = u;
    p[vAxis] = v;
    return p;
  };

  // Zes vlakken, ingezet met de bevelbreedte.
  for (let axis = 0; axis < 3; axis++) {
    const [uAxis, vAxis] = otherAxes(axis);
    for (const sign of [-1, 1]) {
      const u = inset[uAxis]!;
      const v = inset[vAxis]!;
      const corners = [
        point(axis, sign, half[axis]!, -u, -v),
        point(axis, sign, half[axis]!, u, -v),
        point(axis, sign, half[axis]!, u, v),
        point(axis, sign, half[axis]!, -u, v),
      ];
      triangles.push([corners[0]!, corners[1]!, corners[2]!].flat());
      triangles.push([corners[0]!, corners[2]!, corners[3]!].flat());
    }
  }

  // Twaalf randen: elk een quad tussen twee ingezette vlakken.
  for (let a = 0; a < 3; a++) {
    for (let bAxis = a + 1; bAxis < 3; bAxis++) {
      const c = 3 - a - bAxis;
      for (const sa of [-1, 1]) {
        for (const sb of [-1, 1]) {
          const quad = [-1, 1, 1, -1].map((sc, index) => {
            const p = [0, 0, 0];
            const onFaceA = index < 2;
            p[a] = (onFaceA ? half[a]! : inset[a]!) * sa;
            p[bAxis] = (onFaceA ? inset[bAxis]! : half[bAxis]!) * sb;
            p[c] = inset[c]! * sc;
            return p;
          });
          triangles.push([quad[0]!, quad[1]!, quad[2]!].flat());
          triangles.push([quad[0]!, quad[2]!, quad[3]!].flat());
        }
      }
    }
  }

  // Acht hoeken: één driehoek per hoek.
  for (const sx of [-1, 1]) {
    for (const sy of [-1, 1]) {
      for (const sz of [-1, 1]) {
        triangles.push([
          half[0]! * sx, inset[1]! * sy, inset[2]! * sz,
          inset[0]! * sx, half[1]! * sy, inset[2]! * sz,
          inset[0]! * sx, inset[1]! * sy, half[2]! * sz,
        ]);
      }
    }
  }

  return fromTriangles(triangles);
}

/**
 * Driehoekig prisma, liggend langs de Z-as: de basisvorm voor zadeldaken en luifels.
 * De nok loopt over het midden; het prisma staat met zijn onderkant op y = −height/2.
 */
export function prism(width: number, height: number, depth: number): THREE.BufferGeometry {
  const w = width / 2;
  const h = height / 2;
  const d = depth / 2;

  const front: number[][] = [[-w, -h, d], [w, -h, d], [0, h, d]];
  const back: number[][] = [[-w, -h, -d], [w, -h, -d], [0, h, -d]];

  const triangles: number[][] = [
    // Kopse kanten
    [...front[0]!, ...front[1]!, ...front[2]!],
    [...back[0]!, ...back[2]!, ...back[1]!],
    // Twee dakvlakken en de bodem, elk als quad
    ...quad(front[0]!, front[2]!, back[2]!, back[0]!),
    ...quad(front[2]!, front[1]!, back[1]!, back[2]!),
    ...quad(front[1]!, front[0]!, back[0]!, back[1]!),
  ];

  return fromTriangles(triangles, false);
}

/**
 * Zadeldak met overstek. Origin op de onderkant van het dak (y = 0), zodat je het direct
 * op de gevelhoogte kunt zetten.
 */
export function gableRoof(
  width: number,
  depth: number,
  height: number,
  overhang = 0.25,
): THREE.BufferGeometry {
  const geometry = prism(width + overhang * 2, height, depth + overhang * 2);
  geometry.translate(0, height / 2, 0);
  return geometry;
}

/**
 * Amsterdamse trapgevel: een gevel die in symmetrische treden naar een top loopt.
 * Origin op het midden van de onderkant. `steps` telt de treden aan één zijde.
 *
 * Dit is de meest herkenbare silhouetvorm van de grachtenwijk — de reden dat de stad
 * ondanks het New Yorkse grid als Amsterdam leest.
 */
export function steppedGable(
  width: number,
  height: number,
  thickness: number,
  steps = 4,
): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const stepHeight = height / (steps + 1);
  const stepInset = width / 2 / (steps + 1);

  for (let i = 0; i <= steps; i++) {
    const stepWidth = width - i * 2 * stepInset;
    const slab = box(stepWidth, stepHeight, thickness);
    slab.translate(0, stepHeight * (i + 0.5), 0);
    parts.push(slab);
  }

  return concat(parts);
}

/**
 * Cilinder met weinig zijden — 6 tot 8, per stijlgids. Voor palen, bolders, schoorstenen.
 * Origin in het midden; `open` laat de deksels weg als ze toch niet zichtbaar zijn.
 */
export function cylinderN(
  radius: number,
  height: number,
  sides = 6,
  open = false,
): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(radius, radius, height, sides, 1, open).toNonIndexed();
}

/** Afgeknotte kegel: lampenkappen, schoorsteenmutsen, bolderkoppen. */
export function taperedCylinder(
  topRadius: number,
  bottomRadius: number,
  height: number,
  sides = 6,
): THREE.BufferGeometry {
  return new THREE.CylinderGeometry(topRadius, bottomRadius, height, sides, 1).toNonIndexed();
}

/** Plat vlak in het XZ-vlak, kijkend omhoog. Voor water, wegdek, lichtvlekken. */
export function plane(width: number, depth: number): THREE.BufferGeometry {
  const geometry = new THREE.PlaneGeometry(width, depth).toNonIndexed();
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/**
 * Voeg gekleurde onderdelen samen tot één geometrie met vertex-kleuren.
 *
 * Dit is de kern van het draw-call-budget: een heel gebouw of personage-onderdeel wordt
 * één mesh met één gedeeld materiaal (`materials.vertexColored()`), terwijl je toch net
 * zoveel kleurzones houdt als de vorm nodig heeft.
 */
export function mergeParts(parts: Part[]): THREE.BufferGeometry {
  const geometries = parts.map(({ geometry, color: colorName, shade }) => {
    const source = geometry.index ? geometry.toNonIndexed() : geometry;
    const count = source.getAttribute('position').count;
    const rgb = color(colorName);
    const tone = shade ?? 1;
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      colors[i * 3] = Math.min(1, rgb.r * tone);
      colors[i * 3 + 1] = Math.min(1, rgb.g * tone);
      colors[i * 3 + 2] = Math.min(1, rgb.b * tone);
    }
    source.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return source;
  });

  return concat(geometries, true);
}

export function concatGeometries(
  geometries: THREE.BufferGeometry[],
  withColor = false,
): THREE.BufferGeometry {
  return concat(geometries, withColor);
}

// ---------------------------------------------------------------------------
// Interne helpers
// ---------------------------------------------------------------------------

function centerOfRing(ring: FacetedLoftRing): number[] {
  return [ring.offsetX ?? 0, ring.y, ring.offsetZ ?? 0];
}

/**
 * Ringpunten van een loft: per ring `sides` punten op een superellips. Met een spleet
 * lopen de punten van `gap` tot `2π − gap` (beide eindpunten inbegrepen) en sluit de ring
 * niet.
 */
function loftPoints(options: FacetedLoftOptions): number[][][] {
  const sides = options.sides ?? 12;
  const gap = options.gap ?? 0;
  const phase = options.phase ?? 0;
  return options.rings.map((ring) => {
    const n = 2 + (ring.squareness ?? options.squareness ?? 0) * 4;
    const bias = ring.frontBias ?? 0.5;
    const ringGap = gap > 0 ? (ring.gap ?? gap) : 0;
    return Array.from({ length: sides }, (_, index) => {
      const angle =
        ringGap > 0 ? ringGap + (index / (sides - 1)) * (Math.PI * 2 - 2 * ringGap) : phase + (index / sides) * Math.PI * 2;
      const s = Math.sin(angle);
      const c = Math.cos(angle);
      const sx = Math.sign(s) * Math.pow(Math.abs(s), 2 / n);
      const cz = Math.sign(c) * Math.pow(Math.abs(c), 2 / n);
      return [
        (ring.offsetX ?? 0) + sx * ring.width * 0.5,
        ring.y,
        (ring.offsetZ ?? 0) + cz * ring.depth * (cz >= 0 ? bias : 1 - bias),
      ];
    });
  });
}

function pushLoftWalls(triangles: number[][], points: number[][][], open: boolean): void {
  const sides = points[0]!.length;
  for (let ringIndex = 0; ringIndex < points.length - 1; ringIndex++) {
    const lower = points[ringIndex]!;
    const upper = points[ringIndex + 1]!;
    for (let side = 0; side < (open ? sides - 1 : sides); side++) {
      const next = (side + 1) % sides;
      triangles.push([lower[side]!, lower[next]!, upper[next]!].flat());
      triangles.push([lower[side]!, upper[next]!, upper[side]!].flat());
    }
  }
}

function flipWinding(triangles: number[][]): void {
  for (const triangle of triangles) {
    for (let k = 0; k < 3; k++) {
      const b = triangle[3 + k]!;
      triangle[3 + k] = triangle[6 + k]!;
      triangle[6 + k] = b;
    }
  }
}

/** Draai een driehoek zo dat zijn normaal met `toward` meewijst. */
function orient(triangle: number[], toward: number[]): number[] {
  const ax = triangle[3]! - triangle[0]!;
  const ay = triangle[4]! - triangle[1]!;
  const az = triangle[5]! - triangle[2]!;
  const bx = triangle[6]! - triangle[0]!;
  const by = triangle[7]! - triangle[1]!;
  const bz = triangle[8]! - triangle[2]!;
  const nx = ay * bz - az * by;
  const ny = az * bx - ax * bz;
  const nz = ax * by - ay * bx;
  const dot = nx * toward[0]! + ny * toward[1]! + nz * toward[2]!;
  if (dot >= 0) return triangle;
  return [...triangle.slice(0, 3), ...triangle.slice(6, 9), ...triangle.slice(3, 6)];
}

/** De twee assen die niet `axis` zijn, in oplopende volgorde. */
function otherAxes(axis: number): [number, number] {
  if (axis === 0) return [1, 2];
  if (axis === 1) return [0, 2];
  return [0, 1];
}

/** Twee driehoeken uit vier hoekpunten, in de opgegeven volgorde. */
function quad(a: number[], b: number[], c: number[], d: number[]): number[][] {
  return [
    [...a, ...b, ...c],
    [...a, ...c, ...d],
  ];
}

/**
 * Bouw non-indexed geometrie uit losse driehoeken.
 *
 * `fixWinding` draait driehoeken om waarvan de normaal naar het zwaartepunt wijst. Dat werkt
 * voor convexe vormen rond de oorsprong (zoals `beveledBox`) en scheelt het handmatig
 * uitpuzzelen van de winding per facet. Zet hem uit voor niet-convexe vormen.
 */
function fromTriangles(triangles: number[][], fixWinding = true): THREE.BufferGeometry {
  const positions = new Float32Array(triangles.length * 9);
  const a = new THREE.Vector3();
  const b = new THREE.Vector3();
  const c = new THREE.Vector3();
  const edge1 = new THREE.Vector3();
  const edge2 = new THREE.Vector3();
  const normal = new THREE.Vector3();
  const centroid = new THREE.Vector3();

  triangles.forEach((triangle, index) => {
    a.fromArray(triangle, 0);
    b.fromArray(triangle, 3);
    c.fromArray(triangle, 6);

    let flip = false;
    if (fixWinding) {
      edge1.subVectors(b, a);
      edge2.subVectors(c, a);
      normal.crossVectors(edge1, edge2);
      centroid.addVectors(a, b).add(c).divideScalar(3);
      flip = normal.dot(centroid) < 0;
    }

    const offset = index * 9;
    a.toArray(positions, offset);
    (flip ? c : b).toArray(positions, offset + 3);
    (flip ? b : c).toArray(positions, offset + 6);
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
}

/** Plak non-indexed geometrieën aan elkaar. `withColor` neemt ook het color-attribuut mee. */
function concat(geometries: THREE.BufferGeometry[], withColor = false): THREE.BufferGeometry {
  const sources = geometries.map((geometry) => {
    const source = geometry.index ? geometry.toNonIndexed() : geometry;
    if (!source.getAttribute('normal')) source.computeVertexNormals();
    return source;
  });

  let total = 0;
  for (const source of sources) total += source.getAttribute('position').count;

  const positions = new Float32Array(total * 3);
  const normals = new Float32Array(total * 3);
  const colors = withColor ? new Float32Array(total * 3) : null;

  let offset = 0;
  for (const source of sources) {
    const position = source.getAttribute('position');
    const normal = source.getAttribute('normal');
    positions.set(position.array as Float32Array, offset * 3);
    normals.set(normal.array as Float32Array, offset * 3);
    if (colors) {
      const sourceColor = source.getAttribute('color');
      if (sourceColor) colors.set(sourceColor.array as Float32Array, offset * 3);
    }
    offset += position.count;
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  merged.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  if (colors) merged.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return merged;
}
